/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute } from '@angular/router';
import { BaseComponent } from '../../../../../core/components';
import { Component, OnInit } from '@angular/core';
import { DropdownModel, ErrorResponseModel } from '../../../../../core/models';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LbErrorService } from '../../../shared/services/components';
import { LbRbacService } from '../../../shared/services/utilities';
import { LoadingService, LogService, UtilsService, ValidationService } from '../../../../../core/services';
import { NetworkInterfaceModel, RoutingModel } from '../../../shared/models';
import { RoutingApiService } from '../../../shared/http';
import { forkJoin as observableForkJoin, Observable } from 'rxjs';
import { ipValidator } from '../../../../../core/directives';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-routing',
    templateUrl: './routing.component.html',
    styleUrls: ['./routing.component.scss']
})

export class RoutingComponent extends BaseComponent implements OnInit {
    /**
     * Store the routing config data passed through from the resolver.
     */
    public routing: RoutingModel;

    /**
     * Store the network interfaces.
     */
    public interfaces: NetworkInterfaceModel[];

    /**
     * Form group to store the default gateway form.
     */
    public defaultGWForm: FormGroup;

    /**
     * Form group to store the static routes form.
     */
    public staticRoutesForm: FormGroup;

    /**
     * Table row data.
     */
    public tableRows: any[] = [];

    /**
     * Table selected row used when loading in edit mode, highlights selected table row.
     */
    public selectedRow: any = [];

    /**
     * Store interface dropdown array.
     */
    public dropdownInterfaces: any = [];

    /**
     * Store the search keys.
     */
    public searchKeys = ['gatewayIp', 'subnet'];

    /**
     * To store the page lookup for this component.
     * @private
     */
    private pageLookUp: any = {};

    constructor(
        public rbacService: LbRbacService,
        private formBuilder: FormBuilder,
        private lbErrorService: LbErrorService,
        private loadingService: LoadingService,
        private logService: LogService,
        private route: ActivatedRoute,
        private routingApiService: RoutingApiService,
        private utilsService: UtilsService,
    ) {
        super();

        this.routing = this.route.snapshot.data['routingConfig'];
        this.interfaces = this.route.snapshot.data['interfaces'];

        this.setUpDropdowns();
        this.setUpForms();
    }

    /**
     * ngOnInit()
     */
    public ngOnInit(): void {
        this.defaultGWForm.patchValue(this.routing);
        this.setUpPageData();
        this.subscribeToFormChanges();
    }

    /**
     * A check to determine if the button is disabled or not.
     */
    public isButtonDisabled(buttonId: string): boolean {
        if (this.rbacService.isSuperUser) {
            return false;
        } else {
            return this.pageLookUp[buttonId] && this.pageLookUp[buttonId].isDisabled;
        }
    }

    /**
     * Populate table rows for displaying in the table.
     */
    public populateTableRows(): void {
        // Reset our rows variable
        this.tableRows = [];
        let index = 0;
        // Populate our rows with group data
        for (const route of this.routing.staticroutes) {
            this.tableRows.push({
                index: index++,
                id: route.id,
                subnet: route.network.ip + '/' + route.network.mask,
                networkName: route.formNetworkName,
                networkIp: route.network.ip,
                networkMask: route.network.mask,
                gatewayName: route.formGatewayName,
                gatewayIp: route.gateway.ip
            });
            this.tableRows = [...this.tableRows];
        }
    }

    /**
     * Submit Gateway helper function for use with loading button.
     */
    public submitGatewayHelper(): Observable<any> {
        return new Observable((observer) => {
            const actions = this.submitDefaultGW();
            if (actions.length) {
                observableForkJoin(actions)
                    .pipe(
                        takeUntil(this.destroyed$)
                    )
                    .subscribe({
                        next: (): void => {
                        },
                        error: (error: ErrorResponseModel): void => {
                            this.lbErrorService.pageWithErrors(this.defaultGWForm, error.error.errors);
                            this.logService.error('RoutingComponent: submitGatewayHelper', 'Error submitting data', error);
                        },
                        complete: (): void => {
                            observer.next();
                            observer.complete();
                        }
                    });
            } else {
                observer.next();
                observer.complete();
            }
        });
    }

    /**
     * Delete route via API and update the table.
     * @param row
     */
    public deleteRoute(row: { id: any; }): void {
        // check the route is an object
        this.routingApiService.deleteRoute(row.id)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    this.setUpPageData();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('RoutingComponent: deleteRoute', 'Error submitting smtp data', error);
                },
                complete: (): void => {
                    this.loadingService.loadingActionDotsEmitter.next(false);

                }
            });
    }

    /**
     * Get the dropdown content values for the gateway ipv4 interface.
     */
    public setUpDropdowns(): void {
        const convertedArray = [];
        for (const viewInterface of this.interfaces) {
            convertedArray.push(<DropdownModel>{
                key: viewInterface.name, value: viewInterface.name
            });
        }
        this.dropdownInterfaces = convertedArray;
    }

    /**
     * Sets the view to non 'add' state and removes the empty row created.
     */
    public cancelAdd(): void {
        // Set our global add mode state
        this.resetFormState();
    }

    /**
     * Submit new route via the API.
     */
    public submitRoute(): Observable<any> {
        return new Observable((observer) => {
            // Get content of form.
            const network = this.staticRoutesForm.get('network').value;
            const gateway = this.staticRoutesForm.get('via').value;

            this.routingApiService.addRoute(network, gateway)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.setUpPageData();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.lbErrorService.pageWithErrors(this.staticRoutesForm, error.error.errors);
                        this.logService.error('RoutingComponent: addNewRoute', 'Error submitting data', error);
                    },
                    complete: (): void => {
                        observer.next();
                        observer.complete();
                        this.loadingService.loadingButtonEmitter.next(false);
                    }
                });
        });
    }

    /**
     * Set up page data.
     * @private
     */
    private setUpPageData(): void {
        this.staticRoutesForm.reset();
        this.routingApiService.getRoutes()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (response: any): void => {
                    this.routing = response;
                    this.populateTableRows();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('RoutingComponent: updateTable', 'Error submitting data', error);
                }
            });
    }

    /**
     * Submit default gateway.
     * @private
     */
    private submitDefaultGW(): any[] {
        const actions = [];

        // If the ipv4 gateway is not null trim it
        const ipv4gw: any = this.defaultGWForm.get('ipv4gateway').value !== null
            ? this.defaultGWForm.get('ipv4gateway').value.trim()
            : this.defaultGWForm.get('ipv4gateway').value;

        if (ipv4gw === null || ipv4gw === '') {
            if (this.routing.ipv4gateway !== '') {
                actions.push(this.routingApiService.deleteGateway(4));
            }
        } else {
            if (ipv4gw !== this.routing.ipv4gateway ||
                this.defaultGWForm.get('ipv4interface').value !== this.routing.ipv4interface) {
                const ipv4interface = this.defaultGWForm.get('ipv4interface').value;
                const gwData = { gateway: ipv4gw, interface: ipv4interface };
                actions.push(this.routingApiService.setDefaultGateway(gwData));
            }
        }

        // If the ipv6 gateway is not null trim it
        const ipv6gw: any = this.defaultGWForm.get('ipv6gateway').value !== null
            ? this.defaultGWForm.get('ipv6gateway').value.trim()
            : this.defaultGWForm.get('ipv6gateway').value;
        if (ipv6gw === null || ipv6gw === '') {
            if (this.routing.ipv6gateway !== '') {
                actions.push(this.routingApiService.deleteGateway(6));
            }
        } else {
            if (ipv6gw !== this.routing.ipv6gateway ||
                this.defaultGWForm.get('ipv6interface').value !== this.routing.ipv6interface) {
                const ipv6interface = this.defaultGWForm.get('ipv6interface').value;
                const gwData = { gateway: ipv6gw, interface: ipv6interface };
                actions.push(this.routingApiService.setDefaultGateway(gwData));
            }
        }

        return actions;
    }

    /**
     * Reset form state after successful submission.
     * @private
     */
    private resetFormState(): void {
        this.staticRoutesForm.reset();
    }

    /**
     * Set up static route and default gateway forms
     * @private
     */
    private setUpForms(): void {
        this.staticRoutesForm = this.formBuilder.group({
            network: [null, [Validators.required, ValidationService.formIPCIDRSubnetValidator]],
            via: [null, [Validators.required, ipValidator]]
        });

        this.defaultGWForm = this.formBuilder.group({
            ipv4gateway: [null, [ValidationService.formIPV4Check]],
            ipv6gateway: [null, [ValidationService.formIPV6Check]],
            ipv4interface: [null, null],
            ipv6interface: [null, null]
        });
    }

    /**
     * Subscribe to the default gateway form changes.
     * @private
     */
    private subscribeToFormChanges(): void {
        this.defaultGWForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: form => {
                    this.utilsService.debounce('form', () => {
                        if (!this.utilsService.hasFormDataChanged(this.routing, form)) {
                            this.defaultGWForm.markAsPristine();
                        } else {
                            this.defaultGWForm.markAsDirty();
                        }
                    });
                }
            });
    }
}
